import React, { useState, useEffect, useRef } from "react";
import PropTypes from "prop-types";
import classNames from "classnames";
import { AssignmentRulesService } from "@services";
import utils from "@utils";
import { Modal, ReactSelect, ConfirmationModal } from "../../../../components";
import "./StaggeredRuleModal.scss";

const StaggeredRuleModal = props => {
	const { lang } = global;
	const {
		show,
		onHide,
		handleRuleEdit,
		handleRuleDelete,
		assignmentRules,
		staggeredRuleId,
		successCallback,
		user,
	} = props;

	const _confirmationModal = useRef(null);
	const timeValues = [
		{ value: 1, label: "Minutes" },
		{ value: 60, label: "Hours" },
		{ value: 1440, label: "Days" },
	];
	const [activeTab, setActiveTab] = useState("staggered");
	const [assignmentFilter, setAssignmentFilter] = useState("");
	const [filteredAssignmentRules, setFilteredAssignmentRules] = useState([]);
	const [sortAssignmentRulesAsc, setSortAssignmentRulesAsc] = useState(true);
	const [assignedPostingRules, setAssignedPostingRules] = useState([
		{
			value: -1,
			label: "Select a posting rule",
			group: "",
			rule: "",
			postTime: "",
		},
	]);
	const [ruleName, setRuleName] = useState("");
	const [ruleNameError, setRuleNameError] = useState(false);

	useEffect(() => {
		setRuleName("");
		setRuleNameError(false);
		setActiveTab("staggered");
		setAssignedPostingRules([
			{
				value: -1,
				label: "Select a posting rule",
				group: "",
				rule: "",
				postTime: "",
			},
		]);
		if (staggeredRuleId) {
			AssignmentRulesService.getStaggeredRule(staggeredRuleId)
				.then(({ data: staggeredRule }) => {
					setRuleName(staggeredRule.Name);
					const auxList = [];
					for (let i = 0; i < staggeredRule.AssignmentRuleExecutionOrders.length; i++) {
						const newRule = {
							...assignmentRules.find(
								rule =>
									rule.value ===
									staggeredRule.AssignmentRuleExecutionOrders[i].AssignmentRuleId,
							),
						};
						newRule.runTime =
							staggeredRule.AssignmentRuleExecutionOrders[i].TimeIntervalInMinutes;
						newRule.timePeriod = 1;
						const auxTime = newRule.runTime;
						if (auxTime % 60 === 0) {
							newRule.timePeriod = 60;
							newRule.runTime = auxTime / 60;
						}
						if (auxTime % 1440 === 0) {
							newRule.timePeriod = 1440;
							newRule.runTime = auxTime / 1440;
						}
						auxList.push(newRule);
					}
					setAssignedPostingRules(auxList);
				})
				.catch(() =>
					utils.notify({
						type: "error",
						message: lang.t("Error loading staggered Rule"),
						timeOut: 1500,
					}),
				);
		}
	}, [show]);

	useEffect(() => {
		const auxList = [...assignmentRules].sort((a, b) =>
			a.Name.toLowerCase().localeCompare(b.Name.toLowerCase()),
		);
		setFilteredAssignmentRules(auxList);
	}, [assignmentRules]);

	useEffect(() => {
		const filtRules = assignmentRules.filter(x =>
			x.Name.toLowerCase().includes(assignmentFilter.toLowerCase()),
		);
		if (sortAssignmentRulesAsc) {
			filtRules.sort((a, b) => a.Name.toLowerCase().localeCompare(b.Name.toLowerCase()));
		} else {
			filtRules.sort((a, b) => b.Name.toLowerCase().localeCompare(a.Name.toLowerCase()));
		}
		setFilteredAssignmentRules(filtRules);
	}, [assignmentFilter]);

	const assignmentFilterChanged = e => {
		setAssignmentFilter(e.target.value);
	};

	const sortAssignmentRules = () => {
		const filtRules = [...filteredAssignmentRules];
		if (!sortAssignmentRulesAsc) {
			filtRules.sort((a, b) => a.Name.toLowerCase().localeCompare(b.Name.toLowerCase()));
		} else {
			filtRules.sort((a, b) => b.Name.toLowerCase().localeCompare(a.Name.toLowerCase()));
		}
		setFilteredAssignmentRules(filtRules);
		setSortAssignmentRulesAsc(prev => !prev);
	};

	const handlePostingRuleChange = (e, selectedRule) => {
		const value = e?.value;
		const auxAssigned = [...assignedPostingRules];
		const newRule = { ...assignmentRules.find(rule => rule.value === value) };
		const idx = auxAssigned.findIndex(rule => rule === selectedRule);
		auxAssigned[idx] = newRule;
		setAssignedPostingRules(auxAssigned);
	};

	const handleRunTimeChange = (e, selectedRule) => {
		const value = e?.target.value;
		const auxAssigned = [...assignedPostingRules];
		const idx = auxAssigned.findIndex(rule => rule === selectedRule);
		const newRule = { ...auxAssigned[idx] };
		newRule.runTime = value;
		if (newRule.runTimeError) {
			const reg = /^\d+$/;
			if (value !== "" && value != null && reg.test(value)) {
				newRule.runTimeError = false;
			}
		}
		auxAssigned[idx] = newRule;
		setAssignedPostingRules(auxAssigned);
	};

	const handleNameChange = e => {
		const value = e?.target.value;
		setRuleName(value);
		if (ruleNameError) {
			if (value !== "" && value != null) {
				setRuleNameError(false);
			}
		}
	};

	const handleTimePeriodChange = (e, selectedRule) => {
		const value = e?.value;
		const auxAssigned = [...assignedPostingRules];
		const idx = auxAssigned.findIndex(rule => rule === selectedRule);
		auxAssigned[idx].timePeriod = value;
		if (auxAssigned[idx].timePeriodError) {
			if (value !== "" && value != null) {
				auxAssigned[idx].timePeriodError = false;
			}
		}
		setAssignedPostingRules(auxAssigned);
	};

	const validateRules = () => {
		const auxAssigned = [...assignedPostingRules];
		const lastItem = auxAssigned.pop();
		if (lastItem.value === -1) {
			return false;
		}
		return true;
	};

	const validateAllInfo = () => {
		let hasError = false;
		if (ruleName === "" || ruleName == null) {
			hasError = true;
			setRuleNameError(true);
		}
		const auxAssigned = [...assignedPostingRules];
		if (auxAssigned.length < 2) {
			hasError = true;
		}
		for (let i = 0; i < auxAssigned.length; i++) {
			if (auxAssigned[i].value === -1) {
				auxAssigned[i].valueError = true;
				hasError = true;
			}
			if (auxAssigned[i] !== auxAssigned[auxAssigned.length - 1]) {
				const reg = /^\d+$/;
				if (
					auxAssigned[i].runTime == null ||
					!reg.test(auxAssigned[i].runTime) ||
					auxAssigned[i].runTime === 0 ||
					auxAssigned[i].runTime === "0"
				) {
					auxAssigned[i].runTimeError = true;
					hasError = true;
				}
				if (auxAssigned[i].timePeriod == null || auxAssigned[i].timePeriod === "") {
					auxAssigned[i].timePeriodError = true;
					hasError = true;
				}
				if (
					auxAssigned[i].timePeriod != null &&
					auxAssigned[i].timePeriod !== "" &&
					auxAssigned[i].runTime != null &&
					reg.test(auxAssigned[i].runTime)
				) {
					if (
						auxAssigned[i].runTime * auxAssigned[i].timePeriod <
						auxAssigned[i].signUpPeriod + 60
					) {
						auxAssigned[i].runTimeError = true;
						hasError = true;
					}
				}
			}
		}
		setAssignedPostingRules(auxAssigned);
		if (hasError) return false;
		return true;
	};

	const onSave = (updateOnConflict = false) => {
		if (validateAllInfo()) {
			const mappedRules = assignedPostingRules.map(item => ({
				Order: assignedPostingRules.findIndex(obj => obj === item) + 1,
				AssignmentRuleId: item.value,
				TimeIntervalInMinutes: item.runTime ? item.runTime * item.timePeriod : null,
			}));
			const staggeredRule = {
				Id: staggeredRuleId,
				Name: ruleName,
				OrganizationId: user.Organization.Id,
				AssignmentRuleExecutionOrders: mappedRules,
			};
			if (staggeredRuleId == null) {
				staggeredRule.Id = 0;
			}
			if (staggeredRuleId) {
				AssignmentRulesService.updateStaggeredRule({ updateOnConflict }, staggeredRule)
					.then(() => {
						utils.notify({
							type: "success",
							message: lang.t("Staggered rule updated successfully"),
						});
						successCallback();
						onHide();
					})
					.catch(error => {
						const {
							response: { data, status },
						} = error;
						if (status === 409) {
							_confirmationModal.current.openModal({
								title: "Assignment Rules with jobs",
								message: data.Message,
								hideConfirm: false,
								cancelButton: "Cancel",
								onConfirm: () => onSave(true),
							});
						} else {
							utils.notify({
								type: "error",
								message: lang.t("Error updating staggered Rule"),
								timeOut: 1500,
							});
						}
					});
			} else {
				AssignmentRulesService.createStaggeredRule(staggeredRule)
					.then(() => {
						utils.notify({
							type: "success",
							message: lang.t("Staggered rule created successfully"),
						});
						successCallback();
						onHide();
					})
					.catch(() =>
						utils.notify({
							type: "error",
							message: lang.t("Error creating staggered Rule"),
							timeOut: 1500,
						}),
					);
			}
		}
	};

	const removeRule = () => {
		const auxAssigned = [...assignedPostingRules];
		if (assignedPostingRules.length > 1) {
			auxAssigned.pop();
			const idx = auxAssigned.length - 1;
			const prevRule = auxAssigned[idx];
			prevRule.timePeriod = null;
			prevRule.runTime = null;
			auxAssigned[idx] = prevRule;
			setAssignedPostingRules(auxAssigned);
		}
	};

	const addNewRule = () => {
		const auxAssigned = [...assignedPostingRules];
		const prevRule = auxAssigned[auxAssigned.length - 1];
		const newRule = {
			value: -1,
			label: "Select a posting rule",
			group: "",
			rule: "",
			postTime: "",
			runTime: "",
		};
		const prevRuleWithTime = { ...assignmentRules.find(rule => rule.value === prevRule.value) };
		const idx = auxAssigned.length - 1;
		if (prevRuleWithTime.signUpPeriod % 60 === 0) {
			prevRuleWithTime.timePeriod = 60;
			prevRuleWithTime.runTime = (prevRuleWithTime.signUpPeriod + 60) / 60;
		}
		if (prevRuleWithTime.runTime % 1440 === 0) {
			prevRuleWithTime.timePeriod = 1440;
			prevRuleWithTime.runTime = prevRuleWithTime.signUpPeriod / 1440;
		}
		auxAssigned[idx] = prevRuleWithTime;
		if (validateRules()) {
			auxAssigned.push(newRule);
			setAssignedPostingRules(auxAssigned);
		}
	};

	const getRuleList = rule => {
		const auxRulesList = assignmentRules.filter(
			item =>
				item.KeepOriginalBehaviorForAutoAssign === false && item.JobAutoAssignType !== 1,
		);
		if (assignedPostingRules[0] === rule) {
			return auxRulesList;
		}
		return auxRulesList.filter(
			item => item.PostTimeInMinutes === null && item.PostExactDateTime === null,
		);
	};

	return (
		<>
			<Modal
				id="staggered-rule-modal"
				dialogClassName="staggered-rule-dialog"
				size="md"
				show={show}
				onHide={() => onHide()}
			>
				<Modal.Header
					closeButton
					className="hidden-print"
				>
					<Modal.Title>{lang.t("Create Staggered Posting Rule")}</Modal.Title>
				</Modal.Header>
				<Modal.Body className="container-fluid">
					<div>
						<ul
							className="row nav tabs"
							role="presentation"
						>
							<li
								role="presentation"
								className={`col-xs-5 col-xs-offset-1 ${
									activeTab === "assignment-rules" ? " active" : ""
								}`}
							>
								<button
									type="button"
									role="tab"
									name="assignment-rules-tab"
									className="btn-block"
									onClick={() => setActiveTab("assignment-rules")}
								>
									{lang.t("Posting Rules")}
								</button>
							</li>
							<li
								role="presentation"
								className={`col-xs-5 ${activeTab === "staggered" ? " active" : ""}`}
							>
								<button
									type="button"
									role="tab"
									name="staggered-assignment-tab"
									className="btn-block"
									onClick={() => setActiveTab("staggered")}
								>
									{lang.t("Create/Edit Staggered Rule")}
								</button>
							</li>
						</ul>
					</div>
					<If condition={activeTab === "staggered"}>
						{/* Staggered Rule Name */}
						<div className="row">
							<br />
							<div className="col-xs-12">{lang.t("Staggered Rule Name")}</div>
							<div className="col-xs-12 col-sm-7">
								<input
									className={classNames("form-control", {
										"has-error": ruleNameError,
									})}
									name="staggeredRuleName"
									value={ruleName}
									onChange={e => handleNameChange(e)}
								/>
							</div>
						</div>
						{/* Staggered Rule Name */}

						{/* Staggered Rules */}
						<div className="row">
							<br />
							<small className="col-xs-12">
								{lang.t("*A minimum of two posting rules ares required")}
							</small>
							<small className="col-xs-12">
								{lang.t(
									"*Rules that have the “keep open as FCFS” option checked cannot be used in a staggered set",
								)}
							</small>
							<small className="col-xs-12">
								{lang.t(
									"*Rules with Manual Assignment types cannot be used in a staggered set",
								)}
							</small>
							<small className="col-xs-12">
								{lang.t(
									"*Only the first rule can post at a specific time, other rules have to be posted immediately",
								)}
							</small>
							<small className="col-xs-12">
								{lang.t(
									"*Run Time has to be at least 1 hour greater than signup period to allow time for assignments",
								)}
							</small>

							<div className="col-xs-12 rules-section">
								<div className="rules-container">
									<For
										each="rule"
										of={assignedPostingRules}
									>
										<div
											className="row"
											key={`assigned-posting-${rule.value}`}
										>
											<div className="col-xs-6">
												<div
													className={classNames("form-group", {
														"has-error": rule.valueError,
													})}
												>
													<ReactSelect
														options={getRuleList(rule)}
														name="postingRules"
														value={rule.value}
														placeholder="Select a posting rule"
														onChange={e =>
															handlePostingRuleChange(e, rule)
														}
														isDisabled={
															rule !==
															assignedPostingRules[
																assignedPostingRules.length - 1
															]
														}
													/>
												</div>
											</div>
										</div>
										<div className="rule-description-section">
											<ul className="row">
												<li className="col-xs-12">{`Who: ${rule.group}`}</li>
												<li className="col-xs-12">{`Rule: ${rule.rule}`}</li>
												<li className="col-xs-12">{`Post time: ${rule.postTime}`}</li>
											</ul>
										</div>
										<If
											condition={
												rule !==
												assignedPostingRules[
													assignedPostingRules.length - 1
												]
											}
										>
											<div className="row">
												<div className="col-xs-1 text-center rule-text">
													<small>{lang.t("After")}</small>
												</div>
												<div className="col-xs-1 text-center inline-form-control">
													<input
														className={classNames(
															"form-control inline-form-input",
															{
																"has-error": rule.runTimeError,
															},
														)}
														value={rule.runTime}
														onChange={e => handleRunTimeChange(e, rule)}
													/>
												</div>
												<div className="col-xs-4 text-center no-padding-right">
													<div
														className={classNames("form-group", {
															"has-error": rule.timePeriodError,
														})}
													>
														<ReactSelect
															options={timeValues}
															name="timeValues"
															value={rule.timePeriod}
															onChange={e =>
																handleTimePeriodChange(e, rule)
															}
														/>
													</div>
												</div>
												<div className="col-xs-2 text-center inline-subtext">
													<small>{lang.t("run next rule")}</small>
												</div>
											</div>
										</If>
										<If
											condition={
												rule !==
												assignedPostingRules[
													assignedPostingRules.length - 1
												]
											}
										>
											<div className="separator separator-solid" />
										</If>
									</For>
								</div>
							</div>
							<div className="col-xs-6">
								<button
									type="button"
									id="add-new-rule"
									className="btn btn-link"
									onClick={() => addNewRule()}
								>
									{`+ ${lang.t("Add a rule with a run time")}`}
								</button>
							</div>
							<div className="col-xs-6 remove-rule-btn">
								<button
									type="button"
									id="add-new-rule"
									className="btn btn-link remove-rule-btn"
									onClick={() => removeRule()}
								>
									{`- ${lang.t("Remove rule")}`}
								</button>
							</div>
						</div>
						{/* Staggered Rules */}
					</If>
					<If condition={activeTab === "assignment-rules"}>
						<div className="actions-container">
							<input
								className="form-control"
								placeholder="Search..."
								value={assignmentFilter}
								onChange={e => assignmentFilterChanged(e)}
							/>
							<button
								type="button"
								id="add-new-group"
								className="btn btn-link"
								onClick={() => handleRuleEdit()}
							>
								{`+ ${lang.t("Create New Posting Rule")}`}
							</button>
						</div>
						<div className="row assignment-row">
							<div
								className="col-xs-8 col-sm-9"
								role="button"
								onClick={() => sortAssignmentRules()}
								style={{ lineHeight: "32px" }}
								tabIndex="0"
								onKeyPress={({ key }) => {
									if (key === "Enter") sortAssignmentRules();
								}}
							>
								<strong style={{ marginRight: "10px" }}>Rule Name</strong>
								<If condition={sortAssignmentRulesAsc}>
									<i className="glyphicon glyphicon-triangle-bottom" />
								</If>
								<If condition={!sortAssignmentRulesAsc}>
									<i className="glyphicon glyphicon-triangle-top" />
								</If>
							</div>
						</div>
						<For
							each="rule"
							of={filteredAssignmentRules}
						>
							<div
								className="row assignment-row"
								key={`assignment-group-${rule.Id}`}
								style={
									// eslint-disable-next-line no-unsafe-optional-chaining
									filteredAssignmentRules?.findIndex(
										ruleAux => ruleAux.Id === rule.Id,
									) %
										2 ===
									0
										? { background: "#f0f0f0" }
										: { background: "white" }
								}
							>
								<div
									className="col-xs-8 col-sm-9"
									style={{ lineHeight: "32px" }}
								>
									{rule.Name}
								</div>
								<div className="col-xs-2 col-sm-1 col-sm-offset-1 text-center">
									<button
										type="button"
										className="btn btn-link"
										onClick={() => handleRuleEdit(rule.Id)}
									>
										<i className="rkf rkf-pencil" />
									</button>
								</div>
								<div className="col-xs-2 col-sm-1 text-center">
									<button
										type="button"
										className="btn btn-link"
										onClick={() => handleRuleDelete(rule.Id)}
									>
										<i className="rkf rkf-trash text-danger" />
									</button>
								</div>
							</div>
						</For>
					</If>
				</Modal.Body>
				<Modal.Footer>
					<button
						id="button-go-back"
						className="btn btn-link hidden-print"
						type="button"
						onClick={() => onHide()}
					>
						{lang.t("Close")}
					</button>
					<If condition={activeTab === "staggered"}>
						<button
							id="btn-post-job"
							className="btn btn-primary"
							type="button"
							onClick={() => onSave()}
						>
							{lang.t("Save")}
						</button>
					</If>
				</Modal.Footer>
			</Modal>
			<ConfirmationModal ref={_confirmationModal} />
		</>
	);
};

StaggeredRuleModal.propTypes = {
	show: PropTypes.bool,
	onHide: PropTypes.func,
	staggeredRuleId: PropTypes.number,
	assignmentRules: PropTypes.array.isRequired,
	handleRuleDelete: PropTypes.func.isRequired,
	successCallback: PropTypes.func.isRequired,
	handleRuleEdit: PropTypes.func.isRequired,
	user: PropTypes.object.isRequired,
};

StaggeredRuleModal.defaultProps = {
	show: false,
	staggeredRuleId: null,
	onHide: () => {},
};

export default StaggeredRuleModal;
